import { useCallback, useState } from 'react';
import { useSetAtom } from 'jotai';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useFeatureFlag, useHavePermission } from '@/hooks';
import { Permissions } from '@/types';
import { useDeleteInspectionPlanMutation } from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';

type DeleteState = { isOpen: boolean; planId: string | null };

export const useInspectionPlanDelete = () => {
  const [deleteInspectionPlan] = useDeleteInspectionPlanMutation();
  const [deleteState, setDeleteState] = useState<DeleteState>({ isOpen: false, planId: null });
  const setPlans = useSetAtom(state.inspectionPlansState);
  const structureId = useGetStructureId();
  const { showSnackBar } = useSnackBarMessage({});
  const isAdmin = useFeatureFlag('admin');
  const canManageInspectionPlans = useHavePermission(Permissions.MANAGE_INSPECTION_PLANS);

  const canDelete = Boolean(isAdmin || canManageInspectionPlans);

  const handleDelete = useCallback(
    (planId: string) => {
      if (!canDelete) {
        showSnackBar('user does not have permission to delete this view or plan', 'error');
        return;
      }
      setDeleteState({ isOpen: true, planId });
    },
    [canDelete, showSnackBar]
  );

  const cancelDelete = useCallback(() => {
    setDeleteState({ isOpen: false, planId: null });
  }, []);

  const confirmDelete = useCallback(async () => {
    const planId = deleteState.planId;
    if (!planId) return;

    try {
      if (!canDelete) {
        showSnackBar('user does not have permission to delete this view or plan', 'error');
        return;
      }

      await deleteInspectionPlan({
        variables: {
          input: {
            planId,
            structureId,
          },
        },
      });

      setPlans((prev) => prev.filter((v) => v.id !== planId));
      showSnackBar('Plan deleted', 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An error occurred';
      showSnackBar(message, 'error');
    } finally {
      setDeleteState({ isOpen: false, planId: null });
    }
  }, [canDelete, deleteInspectionPlan, deleteState.planId, setPlans, showSnackBar, structureId]);

  return { deleteState, handleDelete, confirmDelete, cancelDelete };
};
